import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  CalendarIcon,
  MapPin,
  Mountain,
  Gauge,
  Camera,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getMyTrip, getTripMembers } from "@/lib/trip.functions";
import { getTripRouteDetailed, getTripDiagnostics, type AdminDiagnostics } from "@/lib/admin.functions";
import { drainNativeLocationQueue } from "@/lib/native/location-bg";
import { PageHeader } from "@/components/admin/PageHeader";
import { RoavaCard, StatusChip, SecondaryButton } from "@/components/roava";
import type { RoutePoint } from "@/components/MediaMap";
// Lazy: pulls in mapbox-gl (~700KB gz). Loaded once the admin map screen renders.
const MediaMap = lazy(() => import("@/components/MediaMap").then((m) => ({ default: m.MediaMap })));
import { useAuth } from "@/lib/auth";
import { TravelerVisibilityControl } from "@/components/library/TravelerVisibilityControl";
import {
  loadAdminTravelerFilter,
  saveAdminTravelerFilter,
  resolveVisibleIds,
  type TravelerFilter,
} from "@/lib/admin-traveler-filter";

type MapSearch = { user?: string };

export const Route = createFileRoute("/_authenticated/admin/map")({
  head: () => pageMeta({ title: 'Route Map — Roava by WGP Apps', description: 'View the trip route and member locations on an interactive map.', path: '/admin/map' }),
  validateSearch: (search: Record<string, unknown>): MapSearch => {
    const out: MapSearch = {};
    if (typeof search.user === "string") out.user = search.user;
    return out;
  },
  component: RoutePage,
});


function RoutePage() {
  const tripFn = useServerFn(getMyTrip);
  const routeFn = useServerFn(getTripRouteDetailed);
  const membersFn = useServerFn(getTripMembers);
  const urlSearch = Route.useSearch();
  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;

  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const [travelerFilter, setTravelerFilter] = useState<TravelerFilter>({
    mode: "all",
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Restore admin filter on trip change.
  useEffect(() => {
    if (!tripId) return;
    const restored = loadAdminTravelerFilter(tripId);
    if (restored.mode === "all" && urlSearch.user) {
      // Back-compat: respect ?user= seed only when no stored filter exists.
      setTravelerFilter({ mode: "selected", ids: [urlSearch.user] });
    } else {
      setTravelerFilter(restored);
    }
  }, [tripId, urlSearch.user]);

  // Persist on change.
  useEffect(() => {
    if (!tripId) return;
    saveAdminTravelerFilter(tripId, travelerFilter);
  }, [tripId, travelerFilter]);

  // Always fetch all travelers — client filtering keeps the popover snappy
  // without refetching when the user toggles members.
  const queryArgs = useMemo(
    () => ({
      tripId: tripId!,
      userId: undefined as string | undefined,
      dateFrom: dateRange?.from ? new Date(dateRange.from).toISOString() : undefined,
      dateTo: dateRange?.to
        ? new Date(new Date(dateRange.to).setHours(23, 59, 59, 999)).toISOString()
        : undefined,
    }),
    [tripId, dateRange],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-route", queryArgs],
    queryFn: () => routeFn({ data: queryArgs }),
    enabled: !!tripId,
    staleTime: 30_000,
  });

  const { data: membersData } = useQuery({
    queryKey: ["trip-members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 60_000,
  });

  const diagFn = useServerFn(getTripDiagnostics);
  const { data: diagData } = useQuery({
    queryKey: ["admin-diagnostics", tripId],
    queryFn: () => diagFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 30_000,
  });
  const diagnosticsByUser = diagData?.byUser ?? {};

  // v4.9 — drain native LocationBG queue on route map mount so admins see the
  // freshest tracks for the currently selected trip.
  useEffect(() => {
    if (tripId) void drainNativeLocationQueue(tripId);
  }, [tripId]);

  const userColors = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const m of membersData?.members ?? []) map[m.userId] = m.color ?? null;
    return map;
  }, [membersData]);
  const userNames = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const m of membersData?.members ?? []) map[m.userId] = m.displayName ?? null;
    return map;
  }, [membersData]);

  const travelers = data?.travelers ?? [];
  const routes = data?.routes ?? [];
  const media = data?.media ?? [];
  const tripStart = data?.tripStartDate ? new Date(data.tripStartDate) : null;

  const travelerOptions = useMemo(
    () =>
      travelers.map((t: any) => ({
        id: t.userId,
        name: t.name,
        avatar: t.avatar ?? null,
      })),
    [travelers],
  );
  const memberIds = useMemo(() => travelerOptions.map((t) => t.id), [travelerOptions]);

  // "Me only" guard: if currentUserId isn't a member, resolveVisibleIds still
  // returns Set([currentUserId]) — items will simply not match and the empty
  // state fires. We never auto-switch back to "all" or mutate the filter.
  const visibleIds = useMemo(
    () => resolveVisibleIds(travelerFilter, memberIds, currentUserId),
    [travelerFilter, memberIds, currentUserId],
  );

  // Non-spammy filter log.
  useEffect(() => {
    const count =
      travelerFilter.mode === "all"
        ? memberIds.length
        : travelerFilter.mode === "me"
          ? currentUserId
            ? 1
            : 0
          : travelerFilter.ids.length;
    console.log("[admin-filter] selected traveler count", count);
  }, [travelerFilter, memberIds.length, currentUserId]);

  // Photo markers for the map — only items with GPS, filtered by admin selection.
  const mediaItems = useMemo(
    () =>
      media
        .filter(
          (m: any) =>
            typeof m.latitude === "number" &&
            typeof m.longitude === "number" &&
            (visibleIds === null || visibleIds.has(m.user_id)),
        )
        .map((m: any) => ({
          id: m.id,
          latitude: m.latitude,
          longitude: m.longitude,
          thumb_url: m.thumb_url ?? null,
          url: m.url ?? null,
          media_type: m.media_type ?? "photo",
          user_id: m.user_id ?? null,
        })),
    [media, visibleIds],
  );

  // Per-user GPS tracks for the colored route overlay.
  const tracksByUser = useMemo(() => {
    const out: Record<string, RoutePoint[]> = {};
    for (const r of routes) {
      if (visibleIds !== null && !visibleIds.has(r.userId)) continue;
      out[r.userId] = r.points
        .filter((p: any) => typeof p.lat === "number" && typeof p.lng === "number")
        .map((p: any) => ({ lat: p.lat, lng: p.lng, capturedAt: p.captured_at ?? undefined }));
    }
    return out;
  }, [routes, visibleIds]);

  const filteredRoutes = useMemo(
    () =>
      visibleIds === null
        ? routes
        : routes.filter((r: any) => visibleIds.has(r.userId)),
    [routes, visibleIds],
  );

  useEffect(() => {
    const total = filteredRoutes.reduce(
      (sum: number, r: any) => sum + (r.points?.length ?? 0),
      0,
    );
    console.log("[admin-route] visible route points", total);
  }, [filteredRoutes]);

  const filterActive = travelerFilter.mode !== "all";

  const selectedMedia = selectedId
    ? media.find((m: any) => m.id === selectedId) ?? null
    : null;

  return (
    <main className="mx-auto max-w-6xl px-5 md:px-8 py-6 md:py-8 space-y-5 pb-nav-safe">
      <PageHeader
        title="Trip route"
        subtitle="Timeline of every traveler's recorded location points."
      />


      <RoavaCard flush className="p-4">
        <div className="flex flex-wrap gap-2">
          <TravelerVisibilityControl
            travelers={travelerOptions}
            meId={currentUserId}
            filter={travelerFilter}
            onChange={setTravelerFilter}
            userColors={userColors}
            labelPrefix="Admin filter: "
          />

          <Popover>
            <PopoverTrigger asChild>
              <SecondaryButton className="gap-2">
                <CalendarIcon className="size-4" />
                {dateRange?.from
                  ? dateRange.to
                    ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}`
                    : format(dateRange.from, "MMM d")
                  : "Any date"}
              </SecondaryButton>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={1}
                className={cn("p-3 pointer-events-auto")}
              />
              {dateRange && (
                <div className="p-2 border-t flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setDateRange(undefined)}>
                    Clear
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </RoavaCard>

      <Suspense
        fallback={
          <div className="w-full aspect-[4/3] md:aspect-[16/9] rounded-2xl bg-muted animate-pulse" />
        }
      >
        <MediaMap
          items={mediaItems}
          onSelect={setSelectedId}
          userColors={userColors}
          userNames={userNames}
          tracksByUser={tracksByUser}
        />
      </Suspense>


      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading route…</p>
      ) : filteredRoutes.length === 0 ? (
        <RoavaCard flush className="p-8 text-center text-sm text-[var(--roava-text-muted)]">
          {filterActive
            ? "No route points for selected travelers"
            : "No location points match the current filters."}
        </RoavaCard>
      ) : (
        <MembersList routes={filteredRoutes} media={media} tripStart={tripStart} diagnosticsByUser={diagnosticsByUser} />
      )}

      <MediaLightbox item={selectedMedia} onClose={() => setSelectedId(null)} />
    </main>
  );
}

function MediaLightbox({ item, onClose }: { item: any | null; onClose: () => void }) {
  const open = !!item;
  const src = item?.url ?? item?.thumb_url ?? null;
  const isVideo = item?.media_type === "video";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-background">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm font-medium truncate">
            {item?.captured_at
              ? format(new Date(item.captured_at), "PP · p")
              : isVideo
              ? "Video"
              : "Photo"}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-muted grid place-items-center max-h-[80vh]">
          {src ? (
            isVideo ? (
              <video src={src} controls className="max-h-[80vh] w-auto" />
            ) : (
              <img src={src} alt="" className="max-h-[80vh] w-auto object-contain" />
            )
          ) : (
            <div className="p-12 text-sm text-muted-foreground">No preview available</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MembersList({
  routes,
  media,
  tripStart,
  diagnosticsByUser,
}: {
  routes: any[];
  media: any[];
  tripStart: Date | null;
  diagnosticsByUser: Record<string, AdminDiagnostics>;
}) {
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  // Initialize newly seen members as open
  useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const r of routes) {
        if (!(r.userId in next)) {
          next[r.userId] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [routes]);

  const allOpen = routes.length > 0 && routes.every((r) => openMap[r.userId]);

  const toggleAll = () => {
    const target = !allOpen;
    setOpenMap(() => {
      const next: Record<string, boolean> = {};
      for (const r of routes) next[r.userId] = target;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <SecondaryButton size="sm" onClick={toggleAll}>
          {allOpen ? "Collapse all" : "Expand all"}
        </SecondaryButton>
      </div>
      {routes.map((r) => (
        <TravelerCard
          key={r.userId}
          route={r}
          media={media}
          tripStart={tripStart}
          diagnostics={diagnosticsByUser[r.userId] ?? null}
          open={openMap[r.userId] ?? false}
          onToggle={() =>
            setOpenMap((prev) => ({ ...prev, [r.userId]: !(prev[r.userId] ?? false) }))
          }
        />
      ))}
    </div>
  );
}

function TravelerCard({
  route,
  media,
  tripStart,
  diagnostics,
  open,
  onToggle,
}: {
  route: any;
  media: any[];
  tripStart: Date | null;
  diagnostics: AdminDiagnostics | null;
  open: boolean;
  onToggle: () => void;
}) {
  const userMedia = useMemo(
    () => media.filter((m) => m.user_id === route.userId && m.captured_at),
    [media, route.userId],
  );

  // Group points by day key (yyyy-mm-dd)
  const days = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const p of route.points) {
      if (!p.captured_at) continue;
      const key = format(new Date(p.captured_at), "yyyy-MM-dd");
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return Array.from(map, ([key, pts]) => ({ key, date: new Date(key), points: pts })).sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );
  }, [route.points]);

  const dayZero = tripStart ?? (days[0]?.date ?? null);
  const stats = route.stats;

  return (
    <RoavaCard flush className="p-5 space-y-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-3 w-full text-left"
        aria-expanded={open}
      >
        <Avatar className="size-9">
          {route.avatar && <AvatarImage src={route.avatar} />}
          <AvatarFallback>{route.name.slice(0, 1)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{route.name}</h3>
          <p className="text-xs text-muted-foreground">
            {stats.totalPoints} location points
          </p>
        </div>
        <ChevronDown className={cn("size-4 transition-transform shrink-0", !open && "-rotate-90")} />
      </button>

      {open && (
        <>


      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
        <Stat label="First point" value={stats.firstAt ? format(new Date(stats.firstAt), "MMM d, HH:mm") : "—"} />
        <Stat label="Last point" value={stats.lastAt ? format(new Date(stats.lastAt), "MMM d, HH:mm") : "—"} />
        <Stat label="Total points" value={String(stats.totalPoints)} />
        <Stat
          label="Distance"
          value={`${stats.distanceKm} km`}
          icon={<MapPin className="size-3.5" />}
        />
        <Stat
          label="Max altitude"
          value={stats.maxAltitude != null ? `${Math.round(stats.maxAltitude)} m` : "—"}
          icon={<Mountain className="size-3.5" />}
        />
        <Stat
          label="Avg speed"
          value={
            stats.avgSpeedMps != null
              ? `${stats.avgSpeedMps.toFixed(2)} m/s · ${(stats.avgSpeedMps * 3.6).toFixed(1)} km/h`
              : "—"
          }
          icon={<Gauge className="size-3.5" />}
        />
        <Stat
          label="Median interval"
          value={stats.medianIntervalSec != null ? `${stats.medianIntervalSec}s between pings` : "—"}
        />
        <Stat
          label="Median move"
          value={stats.medianMoveM != null ? `${stats.medianMoveM} m between pings` : "—"}
        />
      </div>


      <DiagnosticsSection diagnostics={diagnostics} />

      <div className="space-y-2">
        {days.map((d) => (
          <DaySection
            key={d.key}
            day={d}
            dayNumber={dayZero ? differenceInCalendarDays(d.date, dayZero) + 1 : null}
            media={userMedia}
          />
        ))}
      </div>
        </>
      )}
    </RoavaCard>
  );
}

function DaySection({
  day,
  dayNumber,
  media,
}: {
  day: { key: string; date: Date; points: any[] };
  dayNumber: number | null;
  media: any[];
}) {
  const [open, setOpen] = useState(false);

  // Media count for the whole day
  const dayMedia = media.filter((m) => format(new Date(m.captured_at), "yyyy-MM-dd") === day.key);

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="rounded-lg border border-border"
    >
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center gap-2 select-none">
        <ChevronDown className={cn("size-4 transition-transform", !open && "-rotate-90")} />
        <span className="font-medium text-sm">
          {dayNumber ? `Day ${dayNumber}` : format(day.date, "MMM d")} ·{" "}
          <span className="text-muted-foreground font-normal">
            {format(day.date, "EEE, MMM d")}
          </span>
        </span>
        <StatusChip variant="neutral" className="ml-auto">
          {day.points.length} pts
        </StatusChip>
        {dayMedia.length > 0 && (
          <StatusChip variant="info">
            <Camera className="size-3" />
            {dayMedia.length}
          </StatusChip>
        )}
      </summary>

      <ol className="divide-y divide-border">
        {day.points.map((p, i) => {
          const t = new Date(p.captured_at);
          // Nearby media: within ±15 minutes of this point
          const nearby = media.filter((m) => {
            const dt = Math.abs(new Date(m.captured_at).getTime() - t.getTime());
            return dt <= 15 * 60 * 1000;
          });
          return (
            <li key={i} className="px-3 py-2 flex items-center gap-3 text-sm">
              <span className="text-xs text-muted-foreground tabular-nums w-12">
                {format(t, "HH:mm")}
              </span>
              <a
                href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 truncate hover:text-primary inline-flex items-center gap-1"
              >
                {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                <ExternalLink className="size-3 shrink-0" />
              </a>
              {p.altitude_meters != null && (
                <span className="text-xs text-muted-foreground">
                  {Math.round(p.altitude_meters)} m
                </span>
              )}
              {nearby.length > 0 && (
                <StatusChip variant="info">
                  <Camera className="size-3" />
                  {nearby.length}
                </StatusChip>
              )}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function DiagnosticsSection({ diagnostics }: { diagnostics: AdminDiagnostics | null }) {
  if (!diagnostics) {
    return (
      <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
        No diagnostics reported yet. Ask this traveler to open the Route screen so their device can sync.
      </div>
    );
  }
  const d = diagnostics;
  const permTone = (v: boolean | null) =>
    v === true ? "text-success" : v === false ? "text-destructive" : "text-muted-foreground";
  const permLabel = (v: boolean | null) =>
    v === true ? "Granted" : v === false ? "Not granted" : "Unknown";
  const authLabel =
    d.locationAuthLevel === "always" ? "Always" :
    d.locationAuthLevel === "whenInUse" ? "While in use" :
    d.locationAuthLevel === "denied" ? "Denied" :
    d.locationAuthLevel === "prompt" ? "Not set" :
    d.locationAuthLevel ?? "Unknown";
  const authTone =
    d.locationAuthLevel === "always" ? "text-success" :
    d.locationAuthLevel === "denied" ? "text-destructive" :
    "text-foreground";

  const pluginLabel = (state: string) =>
    state === "registered_ok" ? "available" :
    state === "not_registered" ? "not in build" :
    state === "registered_errored" ? "errored" :
    state;
  const pluginTone = (state: string) =>
    state === "registered_ok" ? "text-success" :
    state === "registered_errored" ? "text-amber-600 dark:text-amber-400" :
    state === "not_registered" ? "text-destructive" :
    "text-muted-foreground";

  return (
    <div className="rounded-lg border border-border p-3 space-y-2.5 text-xs">
      <p className="font-semibold text-sm">Device diagnostics</p>
      <div className="space-y-1">
        <Row label="Location permission" value={authLabel} tone={authTone} />
        <Row label="Photos permission" value={permLabel(d.permissions.photosGranted)} tone={permTone(d.permissions.photosGranted)} />
        <Row label="Notifications" value={permLabel(d.permissions.notificationsGranted)} tone={permTone(d.permissions.notificationsGranted)} />
      </div>
      {d.pluginInventory.length > 0 && (
        <div className="pt-2 border-t border-border space-y-1">
          <p className="text-[11px] font-semibold text-muted-foreground">Custom plugin inventory</p>
          {d.pluginInventory.map((p) => (
            <div key={p.name} className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-foreground/80 truncate">
                {p.name}: <span className="text-muted-foreground">{p.probedMethod ?? "(no probe)"}</span>
              </span>
              <span className={`font-medium shrink-0 ${pluginTone(p.state)}`}>{pluginLabel(p.state)}</span>
            </div>
          ))}
        </div>
      )}
      <p className="pt-2 border-t border-border text-[10px] leading-snug text-muted-foreground/70 font-mono">
        App {d.appVersion ?? "?"} (build {d.appBuild ?? "?"}) · OTA {d.otaBuildId ?? "?"}
        {d.updatedAt && ` · synced ${format(new Date(d.updatedAt), "MMM d, HH:mm")}`}
      </p>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${tone}`}>{value}</span>
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-md bg-muted/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-medium text-sm mt-0.5">{value}</div>
    </div>
  );
}


